import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import {
  Users, DollarSign, TrendingUp, Bell, Plus, Search,
  LogOut, Edit2, Trash2, X, Check, Loader2, ChevronUp,
  ChevronDown, Home, BarChart2, UserPlus, Settings,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { springPresets } from '@/lib/motion';
import { useAuth } from '@/contexts/AuthContext';
import { getAllClients, addClient, updateClient, deleteClient, signOut, Client } from '@/lib/auth';
import { ROUTE_PATHS } from '@/lib/index';
import { useToast } from '@/hooks/use-toast';

const PLAN_PRICES: Record<string, number> = {
  'Credit Builder': 197,
  'Wealth Accelerator': 397,
  'Business Empire': 697,
  'Millionaire Mentorship': 1497,
  'Starter': 0,
};

const STATUS_COLORS: Record<string, string> = {
  Active: 'border-primary text-primary',
  Paused: 'border-accent text-accent',
  Cancelled: 'border-destructive text-destructive',
  Completed: 'border-muted-foreground text-muted-foreground',
};

type FormState = {
  name: string; email: string; phone: string; plan: string;
  credit_score: number; starting_score: number;
  status: 'Active' | 'Paused' | 'Cancelled' | 'Completed';
  next_action: string; monthly_payment: number; notes: string; join_date: string;
};

const emptyClient: FormState = {
  name: '', email: '', phone: '', plan: 'Credit Builder',
  credit_score: 0, starting_score: 0, status: 'Active',
  next_action: '', monthly_payment: 197, notes: '', join_date: new Date().toISOString().split('T')[0],
};

export default function AdminPanel() {
  const { user, profile, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(emptyClient);
  const [saving, setSaving] = useState(false);
  const [sortField, setSortField] = useState<keyof Client>('join_date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    if (!authLoading && !user) navigate(ROUTE_PATHS.LOGIN);
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user) loadClients();
  }, [user]);

  async function loadClients() {
    setLoading(true);
    const data = await getAllClients();
    setClients(data);
    setLoading(false);
  }

  async function handleSignOut() {
    await signOut();
    toast({ title: 'Signed out', description: 'See you next time!' });
    navigate(ROUTE_PATHS.LOGIN);
  }

  function openAdd() {
    setForm(emptyClient);
    setShowAddModal(true);
    setEditingClient(null);
  }

  function openEdit(client: Client) {
    setEditingClient(client);
    setForm({
      name: client.name, email: client.email, phone: client.phone || '',
      plan: client.plan, credit_score: client.credit_score,
      starting_score: client.starting_score, status: client.status,
      next_action: client.next_action || '', monthly_payment: client.monthly_payment,
      notes: client.notes || '', join_date: client.join_date,
    });
    setShowAddModal(true);
  }

  async function handleSave() {
    if (!form.name || !form.email) {
      toast({ title: 'Required fields missing', description: 'Name and email are required.', variant: 'destructive' });
      return;
    }
    setSaving(true);
    const payload = { ...form, monthly_payment: PLAN_PRICES[form.plan] || form.monthly_payment };

    if (editingClient) {
      const { error } = await updateClient(editingClient.id, payload);
      if (error) {
        toast({ title: 'Error updating client', description: error.message, variant: 'destructive' });
      } else {
        toast({ title: '✅ Client updated!', description: `${form.name} has been updated.` });
        setShowAddModal(false);
        loadClients();
      }
    } else {
      const { error } = await addClient(payload);
      if (error) {
        toast({ title: 'Error adding client', description: error.message, variant: 'destructive' });
      } else {
        toast({ title: '✅ Client added!', description: `${form.name} is now in your system.` });
        setShowAddModal(false);
        loadClients();
      }
    }
    setSaving(false);
  }

  async function handleDelete(id: string, name: string) {
    setDeletingId(id);
    const { error } = await deleteClient(id);
    if (error) {
      toast({ title: 'Error deleting', description: error.message, variant: 'destructive' });
    } else {
      toast({ title: '🗑️ Client removed', description: `${name} has been deleted.` });
      setClients(prev => prev.filter(c => c.id !== id));
    }
    setDeletingId(null);
  }

  function handleSort(field: keyof Client) {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  }

  const filtered = clients
    .filter(c =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.email.toLowerCase().includes(search.toLowerCase()) ||
      c.plan.toLowerCase().includes(search.toLowerCase())
    )
    .sort((a, b) => {
      const av = a[sortField] ?? '';
      const bv = b[sortField] ?? '';
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

  // KPIs
  const activeClients = clients.filter(c => c.status === 'Active');
  const mrr = activeClients.reduce((sum, c) => sum + Number(c.monthly_payment), 0);
  const avgScore = clients.length ? Math.round(clients.reduce((s, c) => s + c.credit_score, 0) / clients.length) : 0;
  const avgImprovement = clients.length
    ? Math.round(clients.reduce((s, c) => s + (c.credit_score - c.starting_score), 0) / clients.length)
    : 0;

  if (authLoading) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Loader2 className="w-8 h-8 animate-spin text-primary" />
    </div>
  );

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <aside className="w-64 border-r border-border bg-card flex flex-col fixed h-full z-40 hidden lg:flex">
        <div className="p-6 border-b border-border">
          <Link to={ROUTE_PATHS.HOME} className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center">
              <span className="text-white font-bold text-sm">ESA</span>
            </div>
            <div>
              <p className="text-sm font-bold text-foreground">Elevation Systems AI</p>
              <p className="text-[10px] text-muted-foreground">Admin Portal</p>
            </div>
          </Link>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-3 mb-3">Navigation</p>
          {[
            { icon: BarChart2, label: 'Dashboard', active: true },
            { icon: Users, label: 'Clients', active: false },
            { icon: DollarSign, label: 'Revenue', active: false },
            { icon: Settings, label: 'Settings', active: false },
          ].map(item => (
            <button
              key={item.label}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                item.active ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}
            >
              <item.icon className="w-4 h-4" />
              {item.label}
            </button>
          ))}
          <div className="pt-4">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-3 mb-3">Platform</p>
            <Link to={ROUTE_PATHS.HOME}>
              <button className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-all">
                <Home className="w-4 h-4" />
                Public Site
              </button>
            </Link>
          </div>
        </nav>

        <div className="p-4 border-t border-border">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-full bg-primary/20 flex items-center justify-center">
              <span className="text-primary font-bold text-sm">
                {profile?.full_name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || 'A'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground truncate">{profile?.full_name || 'Admin'}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="w-full text-destructive border-destructive/30 hover:bg-destructive/10"
            onClick={handleSignOut}
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 lg:ml-64">
        {/* Top Bar */}
        <div className="sticky top-0 z-30 border-b border-border bg-card/80 backdrop-blur-sm px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">Admin Dashboard</h1>
            <p className="text-xs text-muted-foreground">
              {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" size="icon" className="relative">
              <Bell className="w-4 h-4" />
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-destructive rounded-full text-[10px] flex items-center justify-center text-white">3</span>
            </Button>
            <Button onClick={openAdd} size="sm" className="gap-2">
              <UserPlus className="w-4 h-4" />
              Add Client
            </Button>
            {/* Mobile sign out */}
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden text-destructive"
              onClick={handleSignOut}
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* KPI Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: 'Total Clients', value: clients.length, sub: `${activeClients.length} active`, icon: Users, color: 'text-primary', bg: 'bg-primary/10' },
              { label: 'Monthly Revenue', value: `$${mrr.toLocaleString()}`, sub: 'MRR', icon: DollarSign, color: 'text-accent', bg: 'bg-accent/10' },
              { label: 'Avg Credit Score', value: avgScore, sub: 'across all clients', icon: TrendingUp, color: 'text-primary', bg: 'bg-primary/10' },
              { label: 'Avg Score Gain', value: `+${avgImprovement} pts`, sub: 'from starting score', icon: ChevronUp, color: 'text-accent', bg: 'bg-accent/10' },
            ].map((kpi, i) => (
              <motion.div
                key={kpi.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ ...springPresets.gentle, delay: i * 0.07 }}
              >
                <Card className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <div className={`w-10 h-10 rounded-xl ${kpi.bg} flex items-center justify-center`}>
                      <kpi.icon className={`w-5 h-5 ${kpi.color}`} />
                    </div>
                  </div>
                  <p className="text-2xl font-bold font-mono">{kpi.value}</p>
                  <p className="text-xs text-muted-foreground mt-1">{kpi.label}</p>
                  <p className="text-xs text-muted-foreground">{kpi.sub}</p>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* Revenue by Plan */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Revenue Breakdown by Plan</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {Object.entries(PLAN_PRICES).filter(([k]) => k !== 'Starter').map(([plan, price]) => {
                const count = activeClients.filter(c => c.plan === plan).length;
                const rev = count * price;
                return (
                  <div key={plan} className="bg-muted/30 rounded-xl p-4 border border-border">
                    <p className="text-xs text-muted-foreground mb-1">{plan}</p>
                    <p className="text-xl font-bold text-primary">${rev.toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground">{count} clients × ${price}/mo</p>
                  </div>
                );
              })}
            </div>
          </Card>

          {/* Clients Table */}
          <Card className="p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
              <div>
                <h3 className="text-lg font-semibold">Client Management</h3>
                <p className="text-sm text-muted-foreground">{filtered.length} of {clients.length} clients</p>
              </div>
              <div className="flex items-center gap-3 w-full sm:w-auto">
                <div className="relative flex-1 sm:w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search clients..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <Button onClick={openAdd} size="sm" className="gap-2 whitespace-nowrap">
                  <Plus className="w-4 h-4" />
                  Add Client
                </Button>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-16">
                <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">Loading clients from database...</p>
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground">
                <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="font-medium">No clients found</p>
                <p className="text-sm mt-1">Add your first client to get started</p>
                <Button onClick={openAdd} className="mt-4 gap-2">
                  <UserPlus className="w-4 h-4" />
                  Add Client
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto -mx-6 px-6">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      {[
                        { label: 'Client', field: 'name' },
                        { label: 'Plan', field: 'plan' },
                        { label: 'Score', field: 'credit_score' },
                        { label: 'Gain', field: null },
                        { label: 'Status', field: 'status' },
                        { label: 'MRR', field: 'monthly_payment' },
                        { label: 'Next Action', field: null },
                        { label: '', field: null },
                      ].map(col => (
                        <th
                          key={col.label}
                          className={`text-left py-3 px-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider ${col.field ? 'cursor-pointer hover:text-foreground' : ''}`}
                          onClick={() => col.field && handleSort(col.field as keyof Client)}
                        >
                          <div className="flex items-center gap-1">
                            {col.label}
                            {col.field && sortField === col.field && (
                              sortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                            )}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((client, i) => (
                      <motion.tr
                        key={client.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: i * 0.03 }}
                        className="border-b border-border/50 hover:bg-muted/20 transition-colors group"
                      >
                        <td className="py-3 px-2">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                              <span className="text-primary font-bold text-xs">{client.name[0]}</span>
                            </div>
                            <div>
                              <p className="font-medium text-foreground">{client.name}</p>
                              <p className="text-xs text-muted-foreground">{client.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-2">
                          <Badge variant="outline" className="text-xs whitespace-nowrap">
                            {client.plan}
                          </Badge>
                        </td>
                        <td className="py-3 px-2">
                          <span className="font-mono font-bold text-primary">{client.credit_score}</span>
                        </td>
                        <td className="py-3 px-2">
                          <span className="text-xs font-mono text-primary">
                            +{client.credit_score - client.starting_score}
                          </span>
                        </td>
                        <td className="py-3 px-2">
                          <Badge variant="outline" className={`text-xs ${STATUS_COLORS[client.status]}`}>
                            {client.status}
                          </Badge>
                        </td>
                        <td className="py-3 px-2">
                          <span className="font-mono font-medium">${Number(client.monthly_payment).toLocaleString()}</span>
                        </td>
                        <td className="py-3 px-2 max-w-[200px]">
                          <p className="text-xs text-muted-foreground truncate">{client.next_action || '—'}</p>
                        </td>
                        <td className="py-3 px-2">
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button
                              size="icon"
                              variant="ghost"
                              className="w-7 h-7 text-muted-foreground hover:text-foreground"
                              onClick={() => openEdit(client)}
                            >
                              <Edit2 className="w-3 h-3" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="w-7 h-7 text-destructive hover:bg-destructive/10"
                              onClick={() => handleDelete(client.id, client.name)}
                              disabled={deletingId === client.id}
                            >
                              {deletingId === client.id
                                ? <Loader2 className="w-3 h-3 animate-spin" />
                                : <Trash2 className="w-3 h-3" />
                              }
                            </Button>
                          </div>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowAddModal(false)} />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={springPresets.snappy}
            className="relative w-full max-w-2xl bg-card border border-border rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between p-6 border-b border-border sticky top-0 bg-card z-10">
              <div>
                <h2 className="text-xl font-bold">{editingClient ? 'Edit Client' : 'Add New Client'}</h2>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {editingClient ? `Updating ${editingClient.name}` : 'Fill in client details below'}
                </p>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setShowAddModal(false)}>
                <X className="w-5 h-5" />
              </Button>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Full Name *</label>
                  <Input
                    placeholder="Marcus Johnson"
                    value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Email *</label>
                  <Input
                    type="email"
                    placeholder="client@email.com"
                    value={form.email}
                    onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Phone</label>
                  <Input
                    placeholder="(555) 234-5678"
                    value={form.phone}
                    onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Plan</label>
                  <select
                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                    value={form.plan}
                    onChange={e => setForm(f => ({ ...f, plan: e.target.value, monthly_payment: PLAN_PRICES[e.target.value] || 0 }))}
                  >
                    {Object.keys(PLAN_PRICES).map(p => (
                      <option key={p} value={p}>{p} — ${PLAN_PRICES[p]}/mo</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Current Credit Score</label>
                  <Input
                    type="number"
                    placeholder="700"
                    value={form.credit_score || ''}
                    onChange={e => setForm(f => ({ ...f, credit_score: parseInt(e.target.value) || 0 }))}
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Starting Score</label>
                  <Input
                    type="number"
                    placeholder="580"
                    value={form.starting_score || ''}
                    onChange={e => setForm(f => ({ ...f, starting_score: parseInt(e.target.value) || 0 }))}
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Status</label>
                  <select
                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                    value={form.status}
                    onChange={e => setForm(f => ({ ...f, status: e.target.value as Client['status'] }))}
                  >
                    {['Active', 'Paused', 'Cancelled', 'Completed'].map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Join Date</label>
                  <Input
                    type="date"
                    value={form.join_date}
                    onChange={e => setForm(f => ({ ...f, join_date: e.target.value }))}
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium">Next Action</label>
                <Input
                  placeholder="Review credit report, dispute late payment..."
                  value={form.next_action}
                  onChange={e => setForm(f => ({ ...f, next_action: e.target.value }))}
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium">Notes</label>
                <textarea
                  className="w-full min-h-[80px] px-3 py-2 rounded-md border border-input bg-background text-sm resize-none"
                  placeholder="Any additional notes about this client..."
                  value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                />
              </div>

              <div className="flex items-center justify-between pt-2 border-t border-border">
                <div className="text-sm text-muted-foreground">
                  Monthly payment: <span className="text-primary font-bold">${PLAN_PRICES[form.plan] || form.monthly_payment}/mo</span>
                </div>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => setShowAddModal(false)}>Cancel</Button>
                  <Button onClick={handleSave} disabled={saving} className="gap-2">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                    {editingClient ? 'Save Changes' : 'Add Client'}
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
